import { FileIcon, PlusIcon } from "lucide-react";
import { useNavigate } from "react-router";

import { Column, EmptyDataDash, EmptyState, Text } from "@/components";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { dates } from "@/lib/dates";
import { misc } from "@/lib/misc";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { Campaign, CampaignResponse } from "@/types";

export const CampaignsTable = ({
  campaigns,
}: {
  campaigns: CampaignResponse[];
}) => {
  const navigate = useNavigate();

  const handleRowClick = (campaign: Campaign) => {
    mixpanelUtils.trackRowClick("Campaign", MIXPANEL_SCREENS.CAMPAIGNS);
    void navigate(`/tools/campaigns/${campaign.id}`);
  };

  return (
    <Column className="overflow-auto">
      <div className="rounded-t-lg border border-b-0 border-slate-200">
        <Table>
          <TableHeader>
            <TableRow className="border-none">
              <TableHead className="w-[20%]">
                <Text variant="header">Name</Text>
              </TableHead>
              <TableHead className="w-[12%]">
                <Text variant="header">Status</Text>
              </TableHead>
              <TableHead className="w-[18%]">
                <Text variant="header">Internal Phone Number</Text>
              </TableHead>
              <TableHead className="w-[12%]">
                <Text variant="header">Reached Successfully</Text>
              </TableHead>
              <TableHead className="w-[12%]">
                <Text variant="header">Reached Unsuccessfully</Text>
              </TableHead>
              <TableHead className="w-[13%]">
                <Text variant="header">Sent At</Text>
              </TableHead>
              <TableHead className="w-[13%]">
                <Text variant="header">Created At</Text>
              </TableHead>
            </TableRow>
          </TableHeader>
        </Table>
      </div>
      <div className="overflow-y-auto rounded-b-lg border border-slate-200">
        <Table className="table-fixed">
          <TableBody>
            {campaigns.length === 0 ? (
              <TableRow>
                <TableCell className="p-0" colSpan={7}>
                  <EmptyState
                    className="bg-slate-50 py-14"
                    description="No campaigns were found. Add a campaign to get started!"
                    handlePrimaryClick={() => {
                      mixpanelUtils.trackButtonClick(
                        "Create campaign",
                        MIXPANEL_SCREENS.CAMPAIGNS,
                      );
                      void navigate("/tools/campaigns/create");
                    }}
                    Icon={FileIcon}
                    PrimaryButtonIcon={PlusIcon}
                    primaryButtonName="Create campaign"
                    title="No campaigns found"
                  />
                </TableCell>
              </TableRow>
            ) : (
              campaigns.map((campaign) => {
                const analysis = campaign.campaign.analysis as {
                  reachedSuccessfully?: number;
                  reachedUnsuccessfully?: number;
                } | null;

                return (
                  <TableRow
                    className="cursor-pointer"
                    key={campaign.campaign.id}
                    onClick={() => {
                      handleRowClick(campaign.campaign);
                    }}
                  >
                    <TableCell className="w-[20%]">
                      <Text truncate>{campaign.campaign.name}</Text>
                    </TableCell>
                    <TableCell className="w-[12%]">
                      <Text truncate variant="secondary">
                        {misc.toTitleCase(campaign.campaign.status)}
                      </Text>
                    </TableCell>
                    <TableCell className="w-[18%]">
                      <Text truncate variant="secondary">
                        {campaign.internalPhoneNumber.phone}
                      </Text>
                    </TableCell>
                    <TableCell className="w-[12%]">
                      {analysis?.reachedSuccessfully !== undefined ? (
                        <Text truncate variant="secondary">
                          {analysis.reachedSuccessfully.toString()}
                        </Text>
                      ) : (
                        <EmptyDataDash />
                      )}
                    </TableCell>
                    <TableCell className="w-[12%]">
                      {analysis?.reachedUnsuccessfully !== undefined ? (
                        <Text truncate variant="secondary">
                          {analysis.reachedUnsuccessfully.toString()}
                        </Text>
                      ) : (
                        <EmptyDataDash />
                      )}
                    </TableCell>
                    <TableCell className="w-[13%]">
                      {campaign.campaign.sentAt ? (
                        <Text truncate variant="secondary">
                          {dates.formatIsoDateTimeForUser(
                            campaign.campaign.sentAt,
                          )}
                        </Text>
                      ) : (
                        <EmptyDataDash />
                      )}
                    </TableCell>
                    <TableCell className="w-[13%]">
                      <Text truncate variant="secondary">
                        {dates.formatIsoDateTimeForUser(
                          campaign.campaign.createdAt,
                        )}
                      </Text>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </Column>
  );
};
